/**
 * Custom BarLineComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BarLine as ParentBarLineComponent } from '@shared-lib/chart-components/bar-line/bar-line';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { CustomConfigService } from '../../shared-services/custom-config.service';
import { downloadAsExcel } from '@shared-lib/utils/export.utils';

@Component({
  selector: 'app-bar-line',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bar-line.html',
  styleUrls: ['./bar-line.scss']
})
export class BarLineComponent extends ParentBarLineComponent {
  private readonly noDataText = 'No Data avaliable try changing filters';
  private noDataPatchApplied = false;
  private skipSelfReload = false;
  private rawChartData: any = null;

  // ── Drill state ──────────────────────────────────────────────────────────
  public drillState: 'default' | 'yearly' | 'quarterly' = 'default';
  public isDrilledUp = false;
  private _origCategoryFields: string[] | null = null;
  private _origEnableQuarterYear: boolean | null = null;

  constructor(
    configService: CustomConfigService,
    globalFilterService: GlobalFilterService,
    chartFilterHandler: ChartFilterHandlerService,
  ) {
    super(configService, globalFilterService, chartFilterHandler);
  }

  override ngAfterViewInit(): void {
    this.applyAdvancedPatches();
    super.ngAfterViewInit();
  }

  protected override loadAndRenderChart(filters?: any): void {
    // Global reset: restore drill state so the chart returns to default view
    const isGlobalReset = filters && !filters.widgetId &&
      (filters.changedFilterKey === null || filters.changedFilterKey === undefined);
    if (isGlobalReset && this.drillState !== 'default') {
      this.drillState = 'default';
      this.isDrilledUp = false;
      if (this._origEnableQuarterYear !== null) {
        this.data = {
          ...this.data,
          categoryFields: this._origCategoryFields || this.data?.categoryFields,
        };
        if (this.data.chartConfig) {
          this.data.chartConfig = {
            ...this.data.chartConfig,
            enableQuarterYearAxis: this._origEnableQuarterYear,
          };
        }
        this._origCategoryFields = null;
        this._origEnableQuarterYear = null;
      }
    }

    if (this.skipSelfReload) {
      console.log(`[BarLine] Skipping self-reload for widget ${this.data?.widgetId}`);
      this.skipSelfReload = false;
      this.myChart?.hideLoading();
      return;
    }
    super.loadAndRenderChart(filters);
  }

  private applyAdvancedPatches(): void {
    this.patchNoDataHandling();
    this.patchInteractivity();
  }

  // ── Drill Up: aggregate quarters → show only yearly bars ─────────────────
  public drillUp(): void {
    if (!this.rawChartData || this.drillState === 'yearly') return;

    const barMetric = this.data?.barValueField || this.data?.chartConfig?.barValueField || 'margin';
    const lineMetric = this.data?.lineValueField ?? this.data?.chartConfig?.lineValueField ?? 'percentage';
    const inner = this.rawChartData?.data || this.rawChartData;
    const dataKey = this.data?.dataKey || this.data?.chartConfig?.dataKey;
    let flatRows: any[] = [];
    if (dataKey && Array.isArray(inner?.[dataKey])) flatRows = inner[dataKey];
    else if (Array.isArray(inner)) flatRows = inner;
    if (!flatRows.length) return;

    // Save original config on first drill
    if (this.drillState === 'default') {
      this._origCategoryFields = [...(this.data.categoryFields || ['quarter', 'year'])];
      this._origEnableQuarterYear = this.data?.chartConfig?.enableQuarterYearAxis !== false;
    }

    // Aggregate: sum bar values per year, average line values per year
    const aggMap = new Map<string, { barSum: number; lineSum: number; count: number }>();
    for (const row of flatRows) {
      const year = String(row['year'] || '');
      if (!year) continue;
      const cur = aggMap.get(year) || { barSum: 0, lineSum: 0, count: 0 };
      cur.barSum += (Number(row[barMetric]) || 0);
      cur.lineSum += (Number(row[lineMetric]) || 0);
      cur.count += 1;
      aggMap.set(year, cur);
    }
    const yearlyRows: any[] = [];
    aggMap.forEach((stats, year) => {
      yearlyRows.push({
        year: Number(year),
        [barMetric]: stats.barSum,
        [lineMetric]: stats.count > 0 ? stats.lineSum / stats.count : 0,
      });
    });

    // Temporarily set config for yearly-only view, then render
    const savedRaw = this.rawChartData;
    this.data = { ...this.data, categoryFields: ['year'] };
    if (this.data.chartConfig) {
      this.data.chartConfig = { ...this.data.chartConfig, enableQuarterYearAxis: false };
    }
    this.drillState = 'yearly';
    this.isDrilledUp = true;
    (this as any).selectedCategories = []; // Clear selection state on drill up
    (this as any).initChart(yearlyRows);

    // Restore rawChartData (initChart would have overwritten it in default state)
    this.rawChartData = savedRaw;
    // Restore categoryFields so filter-triggered reloads still use the original config
    this.data = { ...this.data, categoryFields: this._origCategoryFields || ['quarter', 'year'] };
    // Restore highlight state (which should now be empty)
    (this as any).resetCategoryHighlight();
  }

  // ── Drill Down: aggregate years → show only quarter bars (Q1-Q4 summed) ──
  public drillDown(): void {
    if (!this.rawChartData || this.drillState === 'quarterly') return;

    const barMetric = this.data?.barValueField || this.data?.chartConfig?.barValueField || 'margin';
    const lineMetric = this.data?.lineValueField ?? this.data?.chartConfig?.lineValueField ?? 'percentage';
    const inner = this.rawChartData?.data || this.rawChartData;
    const dataKey = this.data?.dataKey || this.data?.chartConfig?.dataKey;
    let flatRows: any[] = [];
    if (dataKey && Array.isArray(inner?.[dataKey])) flatRows = inner[dataKey];
    else if (Array.isArray(inner)) flatRows = inner;
    if (!flatRows.length) return;

    // If drilling down from a specific year selection in Yearly view, filter to those years
    const self = this as any;
    if (this.drillState === 'yearly' && self.selectedCategories?.length > 0) {
      const selectedYears = self.selectedCategories.map(String);
      flatRows = flatRows.filter(row => selectedYears.includes(String(row['year'])));
    }

    // Save original config on first drill
    if (this.drillState === 'default') {
      this._origCategoryFields = [...(this.data.categoryFields || ['quarter', 'year'])];
      this._origEnableQuarterYear = this.data?.chartConfig?.enableQuarterYearAxis !== false;
    }

    // Aggregate: sum bar values per quarter, average line values per quarter
    const aggMap = new Map<string, { barSum: number; lineSum: number; count: number }>();
    const quarterOrder = ['Q1', 'Q2', 'Q3', 'Q4'];
    for (const row of flatRows) {
      const qtr = (String(row['quarter'] || '').match(/Q\d/i)?.[0] || '').toUpperCase();
      if (!qtr) continue;
      const cur = aggMap.get(qtr) || { barSum: 0, lineSum: 0, count: 0 };
      cur.barSum += (Number(row[barMetric]) || 0);
      cur.lineSum += (Number(row[lineMetric]) || 0);
      cur.count += 1;
      aggMap.set(qtr, cur);
    }
    const quarterlyRows: any[] = [];
    aggMap.forEach((stats, qtr) => {
      quarterlyRows.push({
        quarter: qtr,
        [barMetric]: stats.barSum,
        [lineMetric]: stats.count > 0 ? stats.lineSum / stats.count : 0,
      });
    });
    quarterlyRows.sort((a, b) => quarterOrder.indexOf(a.quarter) - quarterOrder.indexOf(b.quarter));

    // Temporarily set config for quarterly-only view, then render
    const savedRaw = this.rawChartData;
    this.data = { ...this.data, categoryFields: ['quarter'] };
    if (this.data.chartConfig) {
      this.data.chartConfig = { ...this.data.chartConfig, enableQuarterYearAxis: false };
    }
    this.drillState = 'quarterly';
    this.isDrilledUp = false;
    (this as any).selectedCategories = []; // Clear selection state on drill down
    (this as any).initChart(quarterlyRows);

    // Restore rawChartData and original config
    this.rawChartData = savedRaw;
    const origQtr = this._origEnableQuarterYear !== null ? this._origEnableQuarterYear : true;
    this.data = { ...this.data, categoryFields: this._origCategoryFields || ['quarter', 'year'] };
    if (this.data.chartConfig) {
      this.data.chartConfig = { ...this.data.chartConfig, enableQuarterYearAxis: origQtr };
    }
  }

  // ── Drill Reset: restore original year+quarter view ────────────────────────
  public drillReset(): void {
    if (!this.rawChartData || this.drillState === 'default') return;
    const origFields = this._origCategoryFields || ['quarter', 'year'];
    const origQtr = this._origEnableQuarterYear !== null ? this._origEnableQuarterYear : true;

    this.data = { ...this.data, categoryFields: origFields };
    if (this.data.chartConfig) {
      this.data.chartConfig = { ...this.data.chartConfig, enableQuarterYearAxis: origQtr };
    }

    this.drillState = 'default';
    this.isDrilledUp = false;
    this._origCategoryFields = null;
    this._origEnableQuarterYear = null;
    (this as any).selectedCategories = []; // Clear selection state on drill reset
    (this as any).initChart(this.rawChartData);
    (this as any).resetCategoryHighlight();
  }

  /**
   * Patch click handling to ensure year labels robustly emit payload
   * and protect against object leakage in filters.
   */
  private patchInteractivity(): void {
    const self = this as any;

    /**
     * Override handleXAxisLabelClick to catch year-axis clicks (axisIndex: 1)
     * and ensure they broadcast a valid filter payload (Year + all Quarters).
     */
    self.handleXAxisLabelClick = (params: any) => {
      const idx = params?.valueIndex ?? params?.dataIndex;
      if (idx == null || idx < 0) return;

      const axisIndex = params?.axisIndex;
      const rawValue = String(params?.value ?? '').trim();
      const isYearValue = /^(19|20)\d{2}$/.test(rawValue);

      const nativeEvent = params.event?.event || params.event;
      const ctrlPressed = self.isCtrlPressed || nativeEvent?.ctrlKey || nativeEvent?.metaKey;

      // Handle Year-Axis Click (Secondary X-Axis or Year-only view)
      if (axisIndex === 1 || isYearValue) {
        const clickedYear = isYearValue ? rawValue : (self.xAxisYearsRaw?.[idx] || this._extractYearLocal(rawValue));
        if (!clickedYear) return;

        // Multi-select logic for years
        const selectedYears = Array.from(new Set((self.selectedCategories || []).map((c: string) => this._extractYearFromCategoryLocal(c)).filter(Boolean)));
        const isYearFullySelected = (self.allCategories || []).filter((c: string) => this._extractYearFromCategoryLocal(c) === clickedYear).every((c: string) => (self.selectedCategories || []).includes(c));

        let nextYears: string[] = [];
        if (ctrlPressed) {
          nextYears = selectedYears.includes(clickedYear)
            ? selectedYears.filter(y => y !== clickedYear)
            : [...selectedYears, clickedYear];
        } else {
          nextYears = (selectedYears.length === 1 && selectedYears[0] === clickedYear && isYearFullySelected)
            ? []
            : [clickedYear];
        }

        const nextCategories = nextYears.length === 0
          ? []
          : (self.allCategories || []).filter((c: string) => nextYears.includes(this._extractYearFromCategoryLocal(c)));

        self.selectedCategories = nextCategories;

        if (self.selectedCategories.length === 0) {
          self.resetCategoryHighlight();
          this.skipSelfReload = false;
          this.emitCombinedSelectionFilters();
          // Trigger manual reload for deselection
          self.loadAndRenderChart(self.globalFilterService.getCurrentFilters());
        } else {
          self.highlightCategories(self.selectedCategories);
          this.skipSelfReload = true;
          this.emitCombinedSelectionFilters();
        }
        return;
      }

      // Handle Quarter-Axis Click (Primary X-Axis)
      const isQuarterValue = /^Q\s*[1-4]$/i.test(rawValue);
      if (axisIndex === 0 || (axisIndex == null && isQuarterValue)) {
        const clickedCategory = self.allCategories[idx];
        if (!clickedCategory) return;

        if (ctrlPressed) {
          const pos = (self.selectedCategories || []).indexOf(clickedCategory);
          self.selectedCategories = pos === -1
            ? [...(self.selectedCategories || []), clickedCategory]
            : (self.selectedCategories || []).filter((c: string) => c !== clickedCategory);
        } else {
          self.selectedCategories = (self.selectedCategories || []).length === 1 && (self.selectedCategories || [])[0] === clickedCategory
            ? []
            : [clickedCategory];
        }

        if (self.selectedCategories.length === 0) {
          self.resetCategoryHighlight();
          this.skipSelfReload = false;
          this.emitCombinedSelectionFilters();
          self.loadAndRenderChart(self.globalFilterService.getCurrentFilters());
        } else {
          self.highlightCategories(self.selectedCategories);
          this.skipSelfReload = true;
          this.emitCombinedSelectionFilters();
        }
        return;
      }

      // Fallback: Use Parent implementation for mixed clicks
      ParentBarLineComponent.prototype['handleXAxisLabelClick'].call(this, params);
    };

    // Re-patch initChart to re-bind the click event after every render
    const originalInitChart = self.initChart;
    self.initChart = (chartData: any) => {
      // Only update rawChartData in default state; drill methods manage it themselves
      if (this.drillState === 'default') {
        (this as any).rawChartData = chartData;
      }
      originalInitChart.apply(this, [chartData]);
      this.centerSingleVisibleYearLabel();

      if (this.myChart) {
        // --- PROTECT AGAINST OBJECT LEAKAGE IN FILTERS ---
        this.myChart.off('click');
        this.myChart.on('click', (params: any) => {
          // Handle x-axis label clicks
          if (params?.componentType === 'xAxis' && (this.data?.chartConfig?.syncXaxisLabels ?? true)) {
            // Access patched method via this or self
            self.handleXAxisLabelClick(params);
            return;
          }

          const nativeEvent = params.event?.event || params.event;
          const ctrlPressed = self.isCtrlPressed || nativeEvent?.ctrlKey || nativeEvent?.metaKey;
          const clickedCategory = params?.data?.category ?? params?.name;

          if (clickedCategory) {
            if (ctrlPressed) {
              const pos = (self.selectedCategories || []).indexOf(clickedCategory);
              self.selectedCategories = pos === -1
                ? [...(self.selectedCategories || []), clickedCategory]
                : (self.selectedCategories || []).filter((c: string) => c !== clickedCategory);
            } else {
              self.selectedCategories = (self.selectedCategories || []).length === 1 && (self.selectedCategories || [])[0] === clickedCategory
                ? []
                : [clickedCategory];
            }

            if (self.selectedCategories.length === 0) {
              self.resetCategoryHighlight();
              this.skipSelfReload = false;
              this.emitCombinedSelectionFilters();
              self.loadAndRenderChart(self.globalFilterService.getCurrentFilters());
            } else {
              self.highlightCategories(self.selectedCategories);
              this.skipSelfReload = true;
              this.emitCombinedSelectionFilters();
            }
            return;
          }

          // Legacy / Fallback payload generation
          if (!this.data?.filterKey) return;
          const filterKeys: string[] = Array.isArray(this.data.filterKey)
            ? this.data.filterKey
            : String(this.data.filterKey).split(',').map((k: string) => k.trim()).filter(Boolean);

          const payload: any = {};
          const categoryKey = this.data?.categoryKey || this.data?.xAxis || 'category';
          const dimensionField = this.data?.dimensionField || this.data?.chartConfig?.dimensionField || 'year';
          const seriesKey = this.data?.seriesKey || 'series';

          filterKeys.forEach((key: string) => {
            if (params?.data && typeof params.data === 'object' && key in params.data) {
              const val = params.data[key];
              if (val && typeof val === 'object' && !Array.isArray(val)) {
                payload[key] = val[key] ?? Object.values(val)[0];
              } else {
                payload[key] = val;
              }
              return;
            }

            if (key === categoryKey || key === 'category') {
              payload[key] = params?.data?.category ?? params?.name;
              return;
            }

            if (key === dimensionField || key === 'year') {
              payload[key] = params?.data?.year ?? params?.data?.[dimensionField] ?? this._extractYearLocal(params?.seriesName);
              return;
            }

            if (key === seriesKey || key === 'series') {
              payload[key] = params?.seriesName;
            }
          });

          if (!Object.keys(payload).length) return;
          this.skipSelfReload = true;
          this.chartFilterHandler.updateFilterSelection(this.data, payload);
        });
      }
    };
  }

  private centerSingleVisibleYearLabel(): void {
    if (!this.myChart) return;

    const option = this.myChart.getOption() as any;
    const xAxes = Array.isArray(option?.xAxis) ? option.xAxis : [];
    const yearAxisData = Array.isArray(xAxes[1]?.data) ? xAxes[1].data : [];
    const visibleYears = Array.from(
      new Set(yearAxisData.map((value: any) => String(value ?? '').trim()).filter(Boolean)),
    );

    if (visibleYears.length === 1 && yearAxisData.length > 1) {
      this.myChart.setOption({ xAxis: [{}, { data: visibleYears }] }, false);
    }
  }

  private _extractYearLocal(input: any): string {
    const match = String(input || '').match(/(19|20)\d{2}/);
    return match ? match[0] : '';
  }

  private emitCombinedSelectionFilters(): void {
    const self = this as any;
    if (!self.selectedCategories || self.selectedCategories.length === 0) {
      const filterKeys = (this.data?.filterKey || []) as string[];
      const qKey = filterKeys.find(k => ['qtr', 'quarter'].includes(k.toLowerCase())) || 'qtr';
      const listener = this.data?.filterListener || this.data?.filterListner;
      const keysToClear = Array.from(
        new Set([
          ...filterKeys,
          'year',
          qKey,
          ...(filterKeys.some(k => ['qtr', 'quarter'].includes(String(k).toLowerCase())) ? ['qtr', 'quarter'] : []),
        ].filter(Boolean)),
      );

      keysToClear.forEach((key, index) => {
        const isLast = index === keysToClear.length - 1;
        if (isLast) {
          this.globalFilterService.resetFilterSelection(key, false, listener);
        } else {
          this.globalFilterService.deleteKey(key, false);
        }
      });
      return;
    }

    const selectedYears = new Set<string>();
    const selectedQuarters = new Set<string>();

    self.selectedCategories.forEach((cat: string) => {
      const meta = self.categoryMetaByCategory?.[cat] || {};
      const q = meta.quarter || this._extractQuarterLocal(cat);
      const y = meta.year || this._extractYearFromCategoryLocal(cat);
      if (q) selectedQuarters.add(q);
      if (y) selectedYears.add(y);
    });

    const yearsArr = Array.from(selectedYears);
    const qtrsArr = Array.from(selectedQuarters);

    const filterKeys = (this.data?.filterKey || []) as string[];
    const qKey = filterKeys.find(k => ['qtr', 'quarter'].includes(k.toLowerCase())) || 'qtr';

    const payload: any = {};
    if (yearsArr.length > 0) {
      payload.year = yearsArr.length === 1 ? yearsArr[0] : yearsArr;
    }
    if (qtrsArr.length > 0) {
      payload[qKey] = qtrsArr.length === 1 ? qtrsArr[0] : qtrsArr;
    }

    const updatedConfig = {
      ...this.data,
      filterKey: [
        ...(yearsArr.length > 0 ? ['year'] : []),
        ...(qtrsArr.length > 0 ? [qKey] : [])
      ]
    };

    this.chartFilterHandler.updateFilterSelection(updatedConfig, payload);
  }

  private _extractYearFromCategoryLocal(cat: string): string {
    const m = String(cat).match(/(19|20)\d{2}/);
    return m ? m[0] : '';
  }

  private _extractQuarterLocal(cat: string): string {
    const str = String(cat ?? '').toUpperCase();
    const qMatch = str.match(/\bQ\s*([1-4])\b/);
    if (qMatch) return `Q${qMatch[1]}`;

    const quarterWordMatch = str.match(/\bQUARTER\s*([1-4])\b/);
    if (quarterWordMatch) return `Q${quarterWordMatch[1]}`;

    const qtrMatch = str.match(/\bQTR\s*([1-4])\b/);
    if (qtrMatch) return `Q${qtrMatch[1]}`;

    return '';
  }

  private patchNoDataHandling(): void {
    if (this.noDataPatchApplied) return;

    const self = this as any;
    const originalInitChart =
      typeof self.initChart === 'function' ? self.initChart.bind(this) : null;

    if (!originalInitChart) {
      return;
    }

    // Wrap the already patched/wrapped initChart from patchInteractivity if it exists
    const currentInitChart = self.initChart;
    self.initChart = (chartData: any) => {
      currentInitChart.apply(this, [chartData]);

      if (this.myChart) {
        const option = this.myChart.getOption() as any;
        if (option && option.series) {
          const series = Array.isArray(option.series) ? option.series : [option.series];
          let changed = false;
          series.forEach((s: any) => {
            if (s.type === 'bar') {
              if (!s.label) s.label = { show: true };
              s.label.fontWeight = 'bold';
              s.label.offset = [0, 5];
              changed = true;
            }
          });
          if (changed) {
            this.myChart.setOption({ series }, false);
          }

          // --- TOOLTIP OVERRIDE FOR BAR-LINE ---
          if (this.data?.widgetId === 'margin-quarterly-trend' || this.data?.widgetId?.includes('Margin')) {
            const formatWithCommas = (val: number) => {
              return isNaN(val) ? '0' : val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
            };

            this.myChart.setOption({
              tooltip: {
                formatter: (items: any) => {
                  const rows = Array.isArray(items) ? items : [items];
                  // The "Q4" or "Q1" label comes from the category name
                  let title = rows[0]?.axisValueLabel || rows[0]?.name || '';
                  if (!title) {
                    try {
                      const c = this.myChart.getOption() as any;
                      title = c?.xAxis?.[0]?.data?.[rows[0]?.dataIndex] || '';
                    } catch (err) { }
                  }

                  const isQuarterly = String(title).toLowerCase().includes('q');

                  // For Quarter: lookup the year from the the axis or data
                  const labelPrefix = isQuarterly ? 'Year Quarter:' : 'Year:';
                  let yearFromData = rows[0]?.data?.year;
                  let labelValue = isQuarterly ? (yearFromData ? `${yearFromData} ${title}` : title) : title;

                  let html = `<div style="font-size: 13px; line-height: 1.5; color: #000;">
                                  <div><b>${labelPrefix}</b> ${labelValue}</div>`;

                  let totalBarRawValue = 0;

                  rows.forEach((point: any) => {
                    const rawValue = point?.data?.rawValue ?? point?.value ?? 0;
                    let sName = point.seriesName;
                    if (sName === 'Margin ($)') sName = 'Margin$';
                    if (sName === 'Margin %') sName = 'Margin%';
                    if (sName === 'Percentage') sName = 'Margin%';

                    // We do not want to strip the percentage from string if they are identical
                    if (point.seriesType === 'line' || sName.includes('Margin%')) {
                      html += `<div><b>${sName}:</b> ${Number(rawValue).toFixed(1)}%</div>`;
                    } else {
                      totalBarRawValue += Number(rawValue);
                      html += `<div><b>${sName}:</b> ${formatWithCommas(Number(rawValue))}</div>`;
                    }
                  });

                  html += `<div><b>Total:</b> ${formatWithCommas(totalBarRawValue)}</div>`;
                  html += `</div>`;
                  return html;
                }
              }
            });
          }
        }
      }

      this.applyNoDataOverlay();
    };

    this.noDataPatchApplied = true;
  }

  private applyNoDataOverlay(): void {
    if (!this.myChart) return;

    const option = (this.myChart.getOption() as any) || {};
    const xAxis = Array.isArray(option.xAxis) ? option.xAxis[0] : option.xAxis;
    const categories: any[] = Array.isArray(xAxis?.data) ? xAxis.data : [];
    const series: any[] = Array.isArray(option.series) ? option.series : [];

    const hasSeriesData = series.some((s: any) =>
      Array.isArray(s?.data) &&
      s.data.some((d: any) => {
        const value = typeof d === 'object' ? d?.value : d;
        return value !== null && value !== undefined && value !== '';
      }),
    );

    const hasData = categories.length > 0 && hasSeriesData;
    const yAxisConfig = Array.isArray(option.yAxis)
      ? option.yAxis.map(() => ({ splitLine: { show: false } }))
      : [{ splitLine: { show: false } }];

    this.myChart.setOption(
      {
        yAxis: yAxisConfig,
        graphic: [
          {
            id: 'no-data-text',
            type: 'text',
            left: 'center',
            top: 'middle',
            silent: true,
            invisible: hasData,
            style: {
              text: this.noDataText,
              fill: '#666',
              fontSize: 14,
              fontFamily: 'sans-serif',
            },
          },
        ],
      },
      false,
    );
  }

  // ─── CSV EXPORT ───────────────────────────────────────────────────────────

  public downloadCsv(): void {
    const rawData = (this as any).rawChartData;
    if (!rawData) return;

    const config = this.data?.chartConfig || {};
    const dataKey = this.data?.dataKey || config.dataKey;
    const barValueField = this.data?.barValueField || config.barValueField || 'gross_value';
    const dimensionField = this.data?.dimensionField || config.dimensionField || 'year';
    const isQuarterly = config.enableQuarterYearAxis === true || (this.data as any).enableQuarterYearAxis === true;

    const inner = rawData?.data || rawData;
    let flatRows: any[] = [];
    if (dataKey && Array.isArray(inner?.[dataKey])) {
      flatRows = inner[dataKey];
    } else if (Array.isArray(inner)) {
      flatRows = inner;
    }

    const dimLabel = (dimensionField || 'year').split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    let headers: string[];
    let csvRows: any[][];

    if (flatRows.length > 0) {
      if (isQuarterly) {
        const grandTotal = flatRows.reduce((s: number, r: any) => s + (Number(r[barValueField]) || 0), 0);
        headers = ['Year', 'Quarter', '%GT Sum', dimLabel, 'Sum of Total Sales'];
        csvRows = flatRows.map((r: any) => {
          const val = Number(r[barValueField]) || 0;
          return [
            r['year'] ?? '',
            r['quarter'] ?? r['qtr'] ?? '',
            grandTotal > 0 ? Math.round((val / grandTotal) * 100) + '%' : '0%',
            r[dimensionField] ?? '',
            Math.round(val)
          ];
        });
      } else {
        headers = ['Year', dimLabel, 'Sum of Total Sales'];
        csvRows = flatRows.map((r: any) => [r['year'] ?? r['Year'] ?? '', r[dimensionField] ?? '', Math.round(Number(r[barValueField]) || 0)]);
      }
    } else if (inner?.category && inner?.series) {
      const categories: string[] = inner.category;
      const seriesList: any[] = inner.series;
      const extractYear = (s: string) => { const m = String(s).match(/(19|20)\d{2}/); return m ? m[0] : ''; };
      const extractQtr = (s: string) => { const m = String(s).toUpperCase().match(/\bQ\s*([1-4])\b/); return m ? `Q${m[1]}` : ''; };
      const grandTotal = seriesList.reduce((s: number, ser: any) =>
        s + (ser.data || []).reduce((ss: number, v: any) => ss + (Number(typeof v === 'object' ? v?.value : v) || 0), 0), 0);

      if (isQuarterly) {
        headers = ['Year', 'Quarter', '%GT Sum', dimLabel, 'Sum of Total Sales'];
        csvRows = [];
        seriesList.forEach((ser: any) => {
          (ser.data || []).forEach((pt: any, i: number) => {
            const cat = categories[i] || '';
            const val = Number(typeof pt === 'object' ? pt?.value : pt) || 0;
            (csvRows as any[]).push([
              extractYear(cat),
              extractQtr(cat),
              grandTotal > 0 ? Math.round((val / grandTotal) * 100) + '%' : '0%',
              ser.name,
              Math.round(val)
            ]);
          });
        });
      } else {
        headers = ['Year', dimLabel, 'Sum of Total Sales'];
        csvRows = [];
        seriesList.forEach((ser: any) => {
          (ser.data || []).forEach((pt: any, i: number) => {
            (csvRows as any[]).push([categories[i] ?? '', ser.name, Math.round(Number(typeof pt === 'object' ? pt?.value : pt) || 0)]);
          });
        });
      }
    } else {
      return;
    }

    downloadAsExcel(String(this.data?.widgetId || 'chart'), headers, csvRows);
  }
}

// Keep backward-compatible export name expected by custom/index.ts and other imports.
export { BarLineComponent as BarLine };
